
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import RatingStars from './RatingStars';

interface SubmitRecommendationFormProps {
  initialData?: any;
  onSubmit: (data: any) => void;
  isSubmitting?: boolean;
}

const SubmitRecommendationForm: React.FC<SubmitRecommendationFormProps> = ({ 
  initialData, 
  onSubmit,
  isSubmitting = false
}) => {
  const [formData, setFormData] = useState({
    coach_name: initialData?.coach_name || '',
    coach_email: initialData?.coach_email || '',
    team_or_school: initialData?.team_or_school || '',
    coach_cell: initialData?.coach_cell || '',
    recommendation_text: initialData?.recommendation_text || '',
    athletic_ability_rating: initialData?.athletic_ability_rating || 0,
    coachability_rating: initialData?.coachability_rating || 0,
    attitude_rating: initialData?.attitude_rating || 0,
    work_ethic_rating: initialData?.work_ethic_rating || 0,
    teammate_rating: initialData?.teammate_rating || 0,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (name: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="coach_name">Your Name*</Label>
          <Input
            id="coach_name"
            name="coach_name"
            value={formData.coach_name}
            onChange={handleChange}
            required
          />
        </div>
        
        <div>
          <Label htmlFor="coach_email">Your Email*</Label>
          <Input
            id="coach_email"
            name="coach_email"
            type="email"
            value={formData.coach_email}
            onChange={handleChange}
            required
          />
        </div>
        
        <div>
          <Label htmlFor="coach_cell">Your Phone Number</Label>
          <Input
            id="coach_cell"
            name="coach_cell"
            type="tel"
            value={formData.coach_cell}
            onChange={handleChange}
          />
        </div>
        
        <div>
          <Label htmlFor="team_or_school">Team/School*</Label>
          <Input
            id="team_or_school"
            name="team_or_school"
            value={formData.team_or_school}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="font-medium">Athlete Ratings</h3>
        
        <div className="space-y-4">
          <div className="flex flex-col gap-1">
            <Label>Athletic Ability*</Label>
            <RatingStars 
              value={formData.athletic_ability_rating} 
              onChange={(value) => handleRatingChange('athletic_ability_rating', value)}
            />
          </div>
          
          <div className="flex flex-col gap-1">
            <Label>Coachability*</Label>
            <RatingStars 
              value={formData.coachability_rating} 
              onChange={(value) => handleRatingChange('coachability_rating', value)}
            />
          </div>
          
          <div className="flex flex-col gap-1">
            <Label>Attitude*</Label>
            <RatingStars 
              value={formData.attitude_rating} 
              onChange={(value) => handleRatingChange('attitude_rating', value)}
            />
          </div>
          
          <div className="flex flex-col gap-1">
            <Label>Work Ethic*</Label>
            <RatingStars 
              value={formData.work_ethic_rating} 
              onChange={(value) => handleRatingChange('work_ethic_rating', value)}
            />
          </div>
          
          <div className="flex flex-col gap-1">
            <Label>Teammate*</Label>
            <RatingStars 
              value={formData.teammate_rating} 
              onChange={(value) => handleRatingChange('teammate_rating', value)}
            />
          </div>
        </div>
      </div>
      
      <div>
        <Label htmlFor="recommendation_text">Recommendation*</Label>
        <Textarea
          id="recommendation_text"
          name="recommendation_text"
          value={formData.recommendation_text}
          onChange={handleChange}
          className="min-h-[150px]"
          placeholder="Please share your assessment of this athlete's abilities, character, and potential..."
          required
        />
      </div>
      
      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          'Submit Recommendation'
        )}
      </Button>
    </form>
  );
};

export default SubmitRecommendationForm;
